import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1",
  timeout: 120000,
});

// Attach app auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("appToken");
  if (token) config.headers["X-App-Token"] = token;
  return config;
});

// API validation details can be objects; UI error state must contain text.
function errorDetailText(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(errorDetailText).filter(Boolean).join("; ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "";
}

// Normalize errors for all screens and clear expired auth tokens.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const data = err.response?.data;
    if (data?.detail != null && typeof data.detail !== "string") {
      data.detail = errorDetailText(data.detail) || "Request failed";
    }
    if (err.response?.status === 401 && !err.config.url?.includes("/app/login")) {
      localStorage.removeItem("appToken");
      window.dispatchEvent(new Event("app-logout"));
    }
    return Promise.reject(err);
  }
);

// ── App auth ──
// The PIN travels in the JSON body, never the query string (query strings land in
// server/proxy access logs); the backend no longer accepts a ?pin= form.
export const appLogin = (pin) => api.post("/app/login", { pin });
export const appVerify = () => api.get("/app/verify");
// A 120 s ticket for the SSE streams (EventSource cannot send the token header), so
// the session token never rides a stream URL — see sse.js.
export const fetchStreamTicket = () => api.post("/app/stream-ticket", null, { timeout: 10000 });

// ── Kite auth ──
export const fetchAuthStatus = () => api.get("/auth/status");
export const fetchLoginUrl = () => api.get("/auth/login-url");

// ── Brokers (default broker runs login, data and orders) ──
export const fetchBrokers = () => api.get("/brokers");
export const fetchBrokerFunds = () => api.get("/brokers/funds");
export const setDefaultBroker = (broker) => api.put("/brokers/default", { broker });
export const startBrokerLogin = (name) => api.post(`/brokers/${name}/login/start`);
export const logoutBroker = (name) => api.post(`/brokers/${name}/logout`);
// server signs in with its own keys — the request carries no credentials. On a bad network the
// server retries (20 s + 60 s backoff) and can take ~3 minutes, so the client waits longer than that.
export const autoLoginBroker = (name) => api.post(`/brokers/${name}/login/auto`, null, { timeout: 200000 });
// a pasted access token ({access_token}) for brokers with manual_token_login; JSON body only, never the
// query string, and the server never echoes it back
export const completeBrokerLogin = (name, body) => api.post(`/brokers/${name}/login/complete`, body);

// ── Dashboard ──
export const fetchDashboardStats = () => api.get("/dashboard/stats");
export const fetchSetupPerformance = (underlying, side, sessionBucket = "") => {
  const params = new URLSearchParams({ underlying, side });
  if (sessionBucket) params.append("session_bucket", sessionBucket);
  return api.get(`/dashboard/setup-performance?${params.toString()}`);
};
export const fetchSentiment = () => api.get("/dashboard/sentiment");
export const fetchOI = () => api.get("/dashboard/oi");
export const fetchBuyerSignal = () => api.get("/dashboard/buyer-signal");
export const fetchBreadth = () => api.get("/dashboard/breadth");
export const fetchMagnitude = () => api.get("/dashboard/magnitude");
// Today's regime readout: volatility backdrop + odds of a bigger-than-usual day (information only, cached 60 s)
export const fetchDayRegime = (underlying = "NIFTY") => api.get(`/dashboard/day-regime?underlying=${underlying}`);
// GARCH range screen (information only): bands for the next session / week / month + accuracy replay.
// model = "lhar" (LHAR+VIX realised-vol regression) or "garch" (GARCH(1,1))
export const fetchGarchCurrent = (underlying = "NIFTY", model = "garch") =>
  api.get(`/garch-range/current?underlying=${underlying}&model=${model}`);
export const fetchGarchReplay = (underlying = "NIFTY", refresh = false, model = "garch") =>
  api.get(`/garch-range/replay?underlying=${underlying}&model=${model}${refresh ? "&refresh=1" : ""}`, { timeout: 120000 });
export const fetchGarchWindow = (underlying, start, end, conf = 0.8, model = "garch") =>
  api.get(`/garch-range/replay/window?underlying=${underlying}&start=${start}&end=${end}&conf=${conf}&model=${model}`);
// GARCH fences (information only): textbook GARCH(1,1) refitted every evening, every daily candle
// with the next-session range drawn the evening before; {status:"running"} while the first fit runs
// model: "garch" (textbook GARCH(1,1)) or "lhar" (LHAR+VIX), both refitted every evening
export const fetchGarchFences = (underlying = "NIFTY", refresh = false, model = "garch") =>
  api.get(`/garch-fences?underlying=${underlying}&model=${model}${refresh ? "&refresh=1" : ""}`);
// option-buyer panel: per session the ATM call / put bought at 09:19 (IV, pay-back levels, 15:20 outcome) + touch odds
export const fetchGarchFenceBuyer = (underlying, model = "garch") =>
  api.get(`/garch-fences/buyer?underlying=${underlying}&model=${model}`);
// the chart's candles (5minute / 15minute / day) from `start`, compact-encoded; {status:"running"} while the cache builds
export const fetchGarchFenceBars = (underlying, tf, start) =>
  api.get(`/garch-fences/bars?underlying=${underlying}&tf=${tf}${start ? `&start=${start}` : ""}`);
// Trend regime: UP / DOWN / SIDEWAYS on the last closed 5-minute bar (read-only context)
export const fetchTrendRegime = (underlying = "NIFTY") => api.get(`/trend-regime/current?underlying=${underlying}`);
// Regime & Technical Structure (EMA / Bollinger / RSI / ADX label on 5-minute spot): live, one past session, a date range
const structureQs = (p) => `underlying=${p.underlying}&fast=${p.fast}&slow=${p.slow}&bb=${p.bb}`;
export const fetchStructureLive = (p) => api.get(`/structure-regime/live?${structureQs(p)}`);
export const fetchStructureSession = (p, day) =>
  api.get(`/structure-regime/session?${structureQs(p)}${day ? `&day=${day}` : ""}`);
export const fetchStructureBacktest = (p, start, end) =>
  api.get(`/structure-regime/backtest?${structureQs(p)}&start=${start}&end=${end}`, { timeout: 120000 });
// OI Profile & Auto Fib: candles + fib of the last `lookback` bars + call/put OI per strike (live, or a past session as of `at`)
const oiFibQs = (p) => `underlying=${p.underlying}&tf=${p.tf}&lookback=${p.lookback}&sessions=${p.sessions}`;
export const fetchOiFibLive = (p) => api.get(`/oi-fib/live?${oiFibQs(p)}`, { timeout: 90000 });
export const fetchOiFibSession = (p, day, at) =>
  api.get(`/oi-fib/session?${oiFibQs(p)}${day ? `&day=${day}` : ""}${at ? `&at=${at}` : ""}`);
// Buyer edge: expected move, breakeven/target moves after costs, theta drag, ATM Greeks,
// session windows, expiry/event context and a verdict with blockers (cached 5 s server-side)
export const fetchBuyerEdge = (underlying = "NIFTY") => api.get(`/dashboard/buyer-edge?underlying=${underlying}`);
export const fetchGreeks = (underlying, strike, optionType) =>
  api.get(`/dashboard/greeks?underlying=${underlying}&strike=${strike}&option_type=${optionType}`);
export const fetchAlertsFeed = (limit = 20) => api.get(`/dashboard/alerts?limit=${limit}`);
export const fetchEvents = (days = 30) => api.get(`/dashboard/events?days=${days}`);
export const partialExitManualTrade = (tradeId, lots = 1) => api.post(`/manual-trader/partial-exit/${tradeId}`, { lots });
// Algo AI — algo_ai's logic as an automated paper strategy
export const fetchAlgoAiStatus = () => api.get("/algo-ai/status");
export const setAlgoAiEnabled = (enabled) => api.post("/algo-ai/config", { enabled });
export const algoAiEvaluateNow = () => api.post("/algo-ai/evaluate-now");
export const fetchAlgoAiCandles = () => api.get("/algo-ai/candles");
export const fetchAlgoAiPrompt = () => api.get("/algo-ai/prompt");
export const testAlgoAiGemini = () => api.post("/algo-ai/test-gemini");

// ── Auto-Trader ──
export const getAutoTraderStatus = () => api.get("/autotrade/status");
export const autotradeToggle = (enabled) => api.post("/autotrade/toggle", { enabled });
export const autotradeHalt = (halt) => api.post("/autotrade/halt", { halt });
export const autotradeSetMode = (paper) => api.post("/autotrade/mode", { paper });
export const autotradeSquareoffAll = (halt = true) => api.post("/autotrade/squareoff-all", { halt });
export const fetchAutotradePositions = () => api.get("/autotrade/positions");
export const fetchAutotradeTrades = (limit = 50) => api.get(`/autotrade/trades?limit=${limit}`);
export const autotradeExit = (tradeId) => api.post(`/autotrade/exit/${tradeId}`);
export const fetchAutotradeDecision = () => api.get("/autotrade/decision");
export const fetchAutotradeActivity = (limit = 40) => api.get(`/autotrade/activity?limit=${limit}`);

// ── Manual AutoTrader (user entries, automated management) ──
export const fetchManualStatus = () => api.get("/manual-trader/status");
export const fetchManualDecision = (underlying = "NIFTY") =>
  api.get(`/manual-trader/decision?underlying=${underlying}`);
export const fetchManualUnderlyings = () => api.get("/manual-trader/underlyings");
export const fetchManualContracts = (search, limit = 30) =>
  api.get(`/manual-trader/contracts?search=${encodeURIComponent(search)}&limit=${limit}`);
export const fetchManualSuggestion = (underlying = "NIFTY") =>
  api.get(`/manual-trader/suggestion?underlying=${underlying}`);
export const fetchManualExpiries = (underlying) =>
  api.get(`/manual-trader/expiries?underlying=${underlying}`);
export const fetchManualStrikes = (underlying, expiry, around = 12) =>
  api.get(`/manual-trader/strikes?underlying=${underlying}&expiry=${expiry}&around=${around}`);
export const fetchManualQuote = (underlying, optionType, strike = null, expiry = null) => {
  const p = new URLSearchParams({ underlying, option_type: optionType });
  if (strike) p.append("strike", strike);
  if (expiry) p.append("expiry", expiry);
  return api.get(`/manual-trader/quote?${p.toString()}`);
};
export const fetchManualQuotes = (symbols) =>
  api.post("/manual-trader/quotes", { symbols });
export const fetchMarginPreview = (payload) => api.post("/manual-trader/margin-preview", payload);
export const placeManualTrade = (payload) => api.post("/manual-trader/place", payload);
export const modifyManualTrade = (tradeId, payload) =>
  api.post(`/manual-trader/modify/${tradeId}`, payload);
export const cancelManualTrade = (tradeId) => api.post(`/manual-trader/cancel/${tradeId}`);
export const exitManualTrade = (tradeId) => api.post(`/manual-trader/exit/${tradeId}`);
export const manualSquareoffAll = () => api.post("/manual-trader/squareoff-all");
export const fetchManualPositions = () => api.get("/manual-trader/positions");
export const fetchManualTrades = (limit = 50) => api.get(`/manual-trader/trades?limit=${limit}`);
export const fetchManualActivity = (limit = 40) => api.get(`/manual-trader/activity?limit=${limit}`);
// Adopt a position you placed on Kite yourself and manage it here
export const fetchBrokerPositions = () => api.get("/manual-trader/broker-positions");
export const adoptBrokerPosition = (payload) => api.post("/manual-trader/adopt", payload);

// ── Pipeline / Data management ──
export const syncInstrumentsPipeline = () => api.post("/pipeline/sync-instruments");
export const searchSymbols = (search, exchange = "NSE", type = "EQ") =>
  api.get(`/pipeline/symbols?search=${search}&exchange=${exchange}&instrument_type=${type}&limit=20`);
export const collectBatch = (symbols, intervals, fromDate = null, toDate = null) =>
  api.post("/pipeline/collect-batch", { symbols, intervals, from_date: fromDate, to_date: toDate }, { timeout: 600000 });
export const collectByDate = (fromDate, toDate, symbols = "NIFTY 50,NIFTY BANK", intervals = "minute,5minute,15minute,60minute,day") =>
  api.post(`/pipeline/collect-by-date?from_date=${fromDate}&to_date=${toDate}&symbols=${encodeURIComponent(symbols)}&intervals=${encodeURIComponent(intervals)}`, {}, { timeout: 600000 });
export const dailyCollectAll = () =>
  api.post("/pipeline/daily-collect-all", {}, { timeout: 1200000 });
export const fetchDataStatusDetailed = () =>
  api.get("/pipeline/data-status-detailed");
export const fetchDataCoverage = () =>
  api.get("/pipeline/data-coverage");
/** Full index / futures / option / chain picture for one calendar date. */
export const fetchCoverageDay = (date) =>
  api.get(`/pipeline/coverage/day?date=${date}`, { timeout: 120000 });
/** First/last stored date per dataset. Slow and date-independent, so it is
 *  fetched separately from the day view and cached server-side for an hour. */
export const fetchCoverageSpans = () =>
  api.get("/pipeline/coverage/spans", { timeout: 180000 });
/** Long-period gap finder: month rollup + the explicit list of missing
 *  session ranges, so a hole across years is one call instead of paging the
 *  120-day calendar. Slow uncached (a few seconds), cached 15 min server-side. */
export const fetchCoverageGaps = (fromDate, toDate, underlying = null, refresh = false) => {
  const p = new URLSearchParams({ from_date: fromDate, to_date: toDate });
  if (underlying) p.append("underlying", underlying);
  if (refresh) p.append("refresh", "true");
  return api.get(`/pipeline/coverage/gaps?${p.toString()}`, { timeout: 180000 });
};
/** Per-day rollup behind the coverage date strip. */
export const fetchCoverageCalendar = (fromDate, toDate) =>
  api.get(`/pipeline/coverage/calendar?from_date=${fromDate}&to_date=${toDate}`, { timeout: 180000 });
export const smartCollect = (symbol, timeframe = "5minute") =>
  api.post(`/pipeline/smart-collect?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}`, {}, { timeout: 300000 });
export const deduplicateData = () =>
  api.post("/pipeline/deduplicate");
export const longRangeFetch = (symbol, interval, fromDate, toDate, chunkDays = 60) =>
  api.post("/pipeline/long-range-fetch", {
    symbol, interval, from_date: fromDate, to_date: toDate, chunk_days: chunkDays
  }, { timeout: 1200000 });
export const collectOptionHistorical = (underlying = "NIFTY", interval = "minute", numStrikes = 10, fromDate = null, toDate = null, daysBack = 5) => {
  let url = `/pipeline/collect-options?underlying=${underlying}&interval=${interval}&num_strikes=${numStrikes}`;
  if (fromDate && toDate) {
    url += `&from_date=${fromDate}&to_date=${toDate}`;
  } else {
    url += `&days_back=${daysBack}`;
  }
  return api.post(url, {}, { timeout: 600000 });
};

// ── Dhan Historical Options (data collection for backtests; trading uses app/brokers/dhan*.py) ──
export const dhanStatus = () => api.get("/pipeline/dhan/status");
export const dhanBuildInstrumentMap = () =>
  api.post("/pipeline/dhan/build-instrument-map", {}, { timeout: 120000 });
export const dhanCollectOptions = (underlying, fromDate, toDate, interval = "1min",
                                    strikesAroundAtm = 5, expiriesAhead = 2) => {
  const params = new URLSearchParams({
    underlying, from_date: fromDate, to_date: toDate, interval,
    strikes_around_atm: strikesAroundAtm, expiries_ahead: expiriesAhead,
  });
  return api.post(`/pipeline/dhan/collect-options?${params.toString()}`, {},
                   { timeout: 1200000 });   // 20 min timeout for long fetches
};
// Backfill EXPIRED options via Dhan's rolling-option endpoint. This is
// what you want for historical backtest windows — it returns up to 5
// years of data without needing per-contract security IDs (which expire
// with their contracts). The default expiryCodes "0,1" fetches both
// near and next-week chronologies so every weekly expiry in the range
// is covered.
export const dhanCollectExpiredOptions = (
  underlying, fromDate, toDate, interval = "1min",
  strikesAroundAtm = 5, expiryFlag = "WEEK", expiryCodes = "0,1",
) => {
  const params = new URLSearchParams({
    underlying, from_date: fromDate, to_date: toDate, interval,
    strikes_around_atm: strikesAroundAtm,
    expiry_flag: expiryFlag, expiry_codes: expiryCodes,
  });
  return api.post(`/pipeline/dhan/collect-expired-options?${params.toString()}`,
                   {}, { timeout: 1800000 });   // 30 min — multi-month fetches
};
export const dhanCoverage = (underlying = null, fromDate = null, toDate = null) => {
  const p = new URLSearchParams();
  if (underlying) p.append("underlying", underlying);
  if (fromDate) p.append("from_date", fromDate);
  if (toDate) p.append("to_date", toDate);
  return api.get(`/pipeline/dhan/coverage?${p.toString()}`);
};

// ── Rules Lab (rule-based trading) ──
export const fetchRuleStrategies = () => api.get("/rules-lab/strategies");
export const fetchRuleUnderlyings = () => api.get("/rules-lab/underlyings");
export const startRuleBacktest = (payload) => api.post("/rules-lab/backtest", payload);
export const fetchRuleJob = (jobId) => api.get(`/rules-lab/jobs/${jobId}`);
// filters: { rule_id, status, underlying, q, result: "profit" | "loss" } — empty values are dropped
export const fetchRuleRuns = (limit = 30, filters = {}) => {
  const p = new URLSearchParams({ limit: String(limit) });
  Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) p.set(k, String(v)); });
  return api.get(`/rules-lab/runs?${p.toString()}`);
};
export const fetchRuleRun = (runId, includeTrades = true) =>
  api.get(`/rules-lab/runs/${runId}?include_trades=${includeTrades}`);
export const deleteRuleRun = (runId) => api.delete(`/rules-lab/runs/${runId}`);
export const fetchRuleReplayDays = (runId) => api.get(`/rules-lab/runs/${runId}/replay-days`);
export const fetchRuleReplay = (runId, day, days = 1) =>
  api.get(`/rules-lab/runs/${runId}/replay?day=${day}&days=${days}`, { timeout: 180000 });
export const fetchRulePresets = () => api.get("/rules-lab/presets");
export const saveRulePreset = (payload) => api.post("/rules-lab/presets", payload);
export const deleteRulePreset = (id) => api.delete(`/rules-lab/presets/${id}`);

// ── Rules engine (live/paper autotrader over every ACTIVE rule) ──
export const fetchRuleAutotradeConfig = () => api.get("/rules-lab/autotrade/config");
export const setRuleAutotradeConfig = (payload) => api.post("/rules-lab/autotrade/config", payload);
export const ruleAutotradeRunNow = () => api.post("/rules-lab/autotrade/run-now");
export const fetchRuleAutotradeStatus = () => api.get("/rules-lab/autotrade/status");
export const setRuleStatus = (id, status) => api.post(`/rules-lab/rules/${id}/status`, { status });

// ── Market regime (indicator vote) ──
export const fetchRegime = (underlying = "NIFTY", timeframe = "5minute") =>
  api.get(`/regime/current?underlying=${underlying}&timeframe=${timeframe}`);

// ── ML Models (copied from mlmarket_dev/frontend/src/api.js, 2026-10-07) ──
export const fetchSignals = (limit = 50) =>
  api.get(`/signals?limit=${limit}`);
export const generateSignal = (symbol = "NIFTY 50") =>
  api.post(`/signals/generate?symbol=${encodeURIComponent(symbol)}`);
export const fetchDeployedModels = () =>
  api.get("/signals/deployed-models");
export const fetchModels = () => api.get("/models");
export const deleteModel = (modelId) => api.delete(`/models/${modelId}`);
export const deployModel = (modelId) => api.post(`/models/${modelId}/deploy`);
export const retireModel = (modelId) => api.post(`/models/${modelId}/retire`);
export const fetchBacktests = () => api.get("/backtests");
export const deleteBacktest = (id) => api.delete(`/backtests/${id}`);
export const deleteAllBacktests = () => api.delete("/backtests");
export const runBacktest = (symbol, startDate, endDate, timeframe, capital = 1000000, numLots = 1, slPct = 30, tgtPct = 50, engineVersion = 1) =>
  api.post(`/backtests/run?symbol=${encodeURIComponent(symbol)}&start_date=${startDate}&end_date=${endDate}&timeframe=${timeframe}&initial_capital=${capital}&num_lots=${numLots}&stop_loss_pct=${slPct}&target_pct=${tgtPct}&engine_version=${engineVersion}`, {}, { timeout: 600000 });
export const trainModel = (symbol, timeframe = "5min", algorithm = "xgboost", deploy = false, startDate = null, endDate = null) => {
  // startDate / endDate (YYYY-MM-DD) = the "trained between" window; empty = every stored candle.
  // 2026-10-11: the server starts the training in the background and answers at once with
  // {job_id, status: "running"} — a 9-year 5-minute run takes 10-30 minutes, longer than any request
  // may wait. Follow it with fetchTrainJob(job_id) until status is "done" (result) or "error".
  const p = new URLSearchParams({ symbol, timeframe, algorithm, deploy });
  if (startDate) p.append("start_date", startDate);
  if (endDate) p.append("end_date", endDate);
  return api.post(`/pipeline/train?${p.toString()}`, {});
};
export const fetchTrainJob = (jobId) => api.get(`/pipeline/train/jobs/${jobId}`);
export const fetchTrainJobs = () => api.get("/pipeline/train/jobs");
export const fetchModelHealth = () => api.get("/model/health");
export const fetchModelMetrics = () => api.get("/model/metrics");
export const fetchModelPerformance = (startDate = null, endDate = null, limit = 90) => {
  const params = new URLSearchParams({ limit });
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  return api.get(`/model/performance?${params.toString()}`);
};
export const fetchModelPredictions = (predictionType = null, startDate = null, endDate = null, limit = 100) => {
  const params = new URLSearchParams({ limit });
  if (predictionType) params.append("prediction_type", predictionType);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  return api.get(`/model/predictions?${params.toString()}`);
};
export const aggregateDailyMetrics = (tradeDate = null) => {
  const params = tradeDate ? `?trade_date=${tradeDate}` : "";
  return api.post(`/model/aggregate-daily${params}`);
};

// ── Settings ──
export const fetchSettings = () => api.get("/settings");
export const updateSetting = (key, value) => api.put("/settings", { key, value });
export const fetchTradingMode = () => api.get("/settings/trading-mode");
export const setTradingMode = (mode) => api.put("/settings/trading-mode", { mode });

// ── System (admin only: process health, soft reload, backend restart) ──
/** Snapshot of the server's own log buffer (already redacted server-side). */
export const fetchSystemLogs = (after = 0, level = "INFO", q = "", limit = 300) => {
  const p = new URLSearchParams({ after, level, limit });
  if (q) p.append("q", q);
  return api.get(`/system/logs?${p.toString()}`);
};
export const clearSystemLogs = () => api.post("/system/logs/clear");
/** Live log stream. Uses fetch, not EventSource, so the X-App-Token header can
 *  be sent — the endpoint stays behind the admin gate instead of needing a
 *  token in the query string. Same reader as dhanStream.js. */
export async function streamSystemLogs(onBatch, { after = 0, level = "INFO", q = "", signal } = {}) {
  const p = new URLSearchParams({ after, level });
  if (q) p.append("q", q);
  const res = await fetch(`${api.defaults.baseURL}/system/logs/stream?${p.toString()}`, {
    headers: { "X-App-Token": localStorage.getItem("appToken") || "", Accept: "text/event-stream" },
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(typeof body.detail === "string" ? body.detail : `Log stream failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      let cut;
      while ((cut = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, cut);
        buffer = buffer.slice(cut + 2);
        const data = frame.split("\n").filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trim()).join("\n");
        if (data) onBatch(JSON.parse(data));      // ": keepalive" frames have no data
      }
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export const fetchSystemStatus = () => api.get("/system/status");
/** Broker data-feed health: REST round-trips, tick rate, build times, verdict. */
export const fetchBrokerHealth = () => api.get("/system/broker-health");
export const fetchTickRecorder = () => api.get("/system/tick-recorder");
/** One real quote round-trip now (for when nothing else is polling the broker). */
export const probeBrokerHealth = () => api.post("/system/broker-health/probe", {}, { timeout: 30000 });
export const systemReload = (parts = null) => api.post("/system/reload", { parts });
export const systemRestart = (force = false) =>
  api.post("/system/restart", { confirm: true, force }, { timeout: 15000 });
/** Unauthenticated liveness ping used to watch the backend come back after a
 *  restart. Plain fetch, not `api`: /health sits outside /api/v1, and a failed
 *  ping must not run the 401 interceptor and log the user out. */
export const pingHealth = async () => {
  const root = api.defaults.baseURL.replace(/\/api\/v1\/?$/, "");
  const res = await fetch(`${root}/health`, { cache: "no-store" });
  if (!res.ok) throw new Error(`health ${res.status}`);
  return res.json();
};

export default api;


// ── Rule Builder: database-driven rule definitions ──
export const fetchRuleIndicators = () => api.get("/rules-lab/rules/indicators");
export const fetchRules = (includeArchived = false) => api.get(`/rules-lab/rules?include_archived=${includeArchived}`);
export const fetchRule = (id) => api.get(`/rules-lab/rules/${id}`);
export const createRule = (payload) => api.post("/rules-lab/rules", payload);
export const updateRule = (id, payload) => api.put(`/rules-lab/rules/${id}`, payload);
export const deleteRule = (id) => api.delete(`/rules-lab/rules/${id}`);
export const duplicateRule = (id) => api.post(`/rules-lab/rules/${id}/duplicate`);
export const backtestRule = (id, payload) => api.post(`/rules-lab/rules/${id}/backtest`, payload);

// ── Selling book (multi-leg short-premium rules) ──
export const fetchSellingCatalog = () => api.get("/selling/catalog");
export const validateSellingStructure = (payload) => api.post("/selling/validate", payload);
export const previewSellingStructure = (payload) => api.post("/selling/preview", payload, { timeout: 60000 });
export const fetchSellingConfig = () => api.get("/selling/config");
export const setSellingConfig = (payload) => api.post("/selling/config", payload);
export const fetchSellingStatus = () => api.get("/selling/status");
export const fetchSellingPositions = () => api.get("/selling/positions");
export const fetchSellingTrades = (limit = 50) => api.get(`/selling/trades?limit=${limit}`);
export const sellingExit = (positionId) => api.post(`/selling/exit/${positionId}`);
export const sellingRunNow = () => api.post("/selling/run-now", {}, { timeout: 120000 });
// one tap: a DRAFT selling rule that shadows a buying rule (credit spread, mirror exits)
export const createCompanionRule = (ruleId, payload = {}) => api.post(`/selling/companion/${ruleId}`, payload);
